// ABOUTME: Admin page for managing email templates with CRUD operations
// ABOUTME: Allows admin users to view, edit, and manage email templates with list and edit forms
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import SharedAppBar from './SharedAppBar';
import { getCurrentAccessToken } from '../services/api';

interface EmailTemplate {
  id: number;
  templateKey: string;
  subjectLine: string;
  htmlContent: string;
  plainTextContent: string;
  isAvailableInCampaigns: boolean;
  campaignCategory: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateEmailTemplateDto {
  templateKey: string;
  subjectLine: string;
  htmlContent: string;
  plainTextContent: string;
  isAvailableInCampaigns: boolean;
  campaignCategory: string | null;
}

interface UpdateEmailTemplateDto {
  subjectLine: string;
  htmlContent: string;
  plainTextContent: string;
  isAvailableInCampaigns: boolean;
  campaignCategory: string | null;
}

const AdminEmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Form state
  const [formData, setFormData] = useState<CreateEmailTemplateDto>({
    templateKey: '',
    subjectLine: '',
    htmlContent: '',
    plainTextContent: '',
    isAvailableInCampaigns: false,
    campaignCategory: null
  });

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/admin/email-templates', {
        headers: {
          'Authorization': `Bearer ${getCurrentAccessToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load email templates');
      }

      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading email templates:', error);
      showSnackbar('Failed to load email templates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsCreating(false);
    setFormData({
      templateKey: template.templateKey,
      subjectLine: template.subjectLine,
      htmlContent: template.htmlContent,
      plainTextContent: template.plainTextContent,
      isAvailableInCampaigns: template.isAvailableInCampaigns,
      campaignCategory: template.campaignCategory
    });
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setIsCreating(true);
    setFormData({
      templateKey: '',
      subjectLine: '',
      htmlContent: '',
      plainTextContent: '',
      isAvailableInCampaigns: false,
      campaignCategory: null
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        // Create new template
        const response = await fetch('/api/admin/email-templates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCurrentAccessToken()}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error('Failed to create email template');
        }

        showSnackbar('Email template created successfully', 'success');
      } else {
        // Update existing template
        const updateData: UpdateEmailTemplateDto = {
          subjectLine: formData.subjectLine,
          htmlContent: formData.htmlContent,
          plainTextContent: formData.plainTextContent,
          isAvailableInCampaigns: formData.isAvailableInCampaigns,
          campaignCategory: formData.campaignCategory
        };

        const response = await fetch(`/api/admin/email-templates/${selectedTemplate?.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCurrentAccessToken()}`
          },
          body: JSON.stringify(updateData)
        });

        if (!response.ok) {
          throw new Error('Failed to update email template');
        }

        showSnackbar('Email template updated successfully', 'success');
      }

      setEditDialogOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error('Error saving email template:', error);
      showSnackbar('Failed to save email template', 'error');
    }
  };

  const handleDelete = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await fetch(`/api/admin/email-templates/${selectedTemplate.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getCurrentAccessToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete email template');
      }

      showSnackbar('Email template deleted successfully', 'success');
      setDeleteDialogOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting email template:', error);
      showSnackbar('Failed to delete email template', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTemplates intentionally called once on mount
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title="Email Templates" />
        <Box sx={{ flex: 1, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Loading email templates...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar title="Email Templates" />
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Email Templates Management</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
          >
            Create Template
          </Button>
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Template Key</TableCell>
                    <TableCell>Subject Line</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <Chip label={template.templateKey} size="small" />
                      </TableCell>
                      <TableCell>{template.subjectLine}</TableCell>
                      <TableCell>{formatDate(template.createdAt)}</TableCell>
                      <TableCell>{formatDate(template.updatedAt)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(template)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(template)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Edit/Create Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isCreating ? 'Create Email Template' : 'Edit Email Template'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {isCreating && (
                <TextField
                  label="Template Key"
                  value={formData.templateKey}
                  onChange={(e) => setFormData({ ...formData, templateKey: e.target.value })}
                  fullWidth
                  required
                  helperText="Unique identifier for the template (e.g., 'welcome', 'password_reset')"
                />
              )}
              <TextField
                label="Subject Line"
                value={formData.subjectLine}
                onChange={(e) => setFormData({ ...formData, subjectLine: e.target.value })}
                fullWidth
                required
                helperText="Email subject with placeholders like {{UserName}}"
              />
              <TextField
                label="HTML Content"
                value={formData.htmlContent}
                onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                fullWidth
                multiline
                rows={6}
                required
                helperText="HTML version of the email template"
              />
              <TextField
                label="Plain Text Content"
                value={formData.plainTextContent}
                onChange={(e) => setFormData({ ...formData, plainTextContent: e.target.value })}
                fullWidth
                multiline
                rows={6}
                required
                helperText="Plain text version of the email template"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isAvailableInCampaigns}
                    onChange={(e) => setFormData({ ...formData, isAvailableInCampaigns: e.target.checked })}
                  />
                }
                label="Show in campaigns"
              />

              <FormControl fullWidth>
                <InputLabel>Campaign Category</InputLabel>
                <Select
                  value={formData.campaignCategory || ''}
                  onChange={(e) => setFormData({ ...formData, campaignCategory: e.target.value || null })}
                  label="Campaign Category"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Onboarding">Onboarding</MenuItem>
                  <MenuItem value="Trial">Trial</MenuItem>
                  <MenuItem value="Subscription">Subscription</MenuItem>
                  <MenuItem value="Policies">Policies</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Educational">Educational</MenuItem>
                  <MenuItem value="Uncategorized">Uncategorized</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {isCreating ? 'Create' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Email Template</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the email template &quot;{selectedTemplate?.templateKey}&quot;?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default AdminEmailTemplatesPage;